import React, { useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import IconButton from "@material-ui/core/IconButton";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import InfoIcon from "@material-ui/icons/Info";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CheckIcon from "@material-ui/icons/Check";
import moment from "moment";
import { Chip, Paper, TextField, InputAdornment } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { useDispatch, useSelector } from "react-redux";

import { useHistory } from "react-router-dom";
//CORE
import MaterialDataGridTable from "../MaterialDataGridTable/index";
import { getOrders } from "redux/action/order.action";
import { getSubscriptions } from "redux/action/customer.action";
import Loading from "components/Loading";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import NoData from "assets/img/NoData.svg";
import { getDisplaySubscriptionValue } from "utils/subscriptionAmount";
import { isBoolean, isEmpty, round, split, uniqBy } from "lodash";

const STATUSES = [
  { value: "processing", label: "Pendente", color: "#f57c00" },
  { value: "confirmed", label: "Confirmado", color: "#1976d2" },
  { value: "completed", label: "Concluído", color: "#388e3c" },
  { value: "cancelled", label: "Cancelado", color: "#d32f2f" },
];

const FREQUENCIES = [
  { value: "monthly", label: "Mensal" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semester", label: "Semestral" },
  { value: "annual", label: "Anual" },
];

const DEFAULT_FILTERS = {
  statuses: [],
  dateFrom: "",
  dateTo: "",
  m2Min: "",
  m2Max: "",
  frequencies: [],
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2)
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    marginTop: "10px"
  },
  filterBar: {
    padding: "14px 16px",
    marginBottom: 16,
    borderRadius: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    background: "#fff",
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 10,
    "&:last-child": { marginBottom: 0 },
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#666",
    minWidth: 80,
    flexShrink: 0,
  },
  filterChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  resultCount: {
    fontSize: 12,
    color: "#888",
    marginLeft: "auto",
  },
  clearBtn: {
    fontSize: 12,
    color: "#1976d2",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: "2px 6px",
    borderRadius: 4,
    "&:hover": { background: "#e3f2fd" },
  },
}));

function toggleItem(arr, val) {
  return arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
}

export default function EnhancedTable(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const ORDER_STATE = useSelector(state => state.order);
  const CUSTOMER_STATE = useSelector(state => state.customer);
  const subscriptions = useMemo(() => CUSTOMER_STATE?.subscriptions || [], [CUSTOMER_STATE.subscriptions]);
  let orders = useMemo(() => {
    if (!ORDER_STATE?.orders) return [];
    return ORDER_STATE.orders.map(order => {
      const allItems = order?.products?.flatMap(product => product?.items || []) || [];
      const uniqueItems = uniqBy(allItems, '_id');
      const itemsCount = order?.items_count || uniqueItems.length;
      return { ...order, items_count: itemsCount };
    });
  }, [ORDER_STATE]);

  const [rows, setRows] = React.useState([]);
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);

  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      // Sem filtro de estado: esconde os cancelados (para os ver, escolher "Cancelado")
      if (filters.statuses.length === 0 && row.status === "cancelled") return false;
      if (filters.statuses.length > 0 && !filters.statuses.includes(row.status)) return false;
      if (filters.dateFrom && row.pickup_date < filters.dateFrom) return false;
      if (filters.dateTo && row.pickup_date > filters.dateTo) return false;
      if (filters.m2Min !== "" && (row.size_m2 === "-" || Number(row.size_m2) < Number(filters.m2Min))) return false;
      if (filters.m2Max !== "" && (row.size_m2 === "-" || Number(row.size_m2) > Number(filters.m2Max))) return false;
      if (filters.frequencies.length > 0 && !filters.frequencies.includes(row.payment_frequency)) return false;
      return true;
    });
  }, [rows, filters]);

  const hasActiveFilters =
    filters.statuses.length > 0 ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.m2Min !== "" ||
    filters.m2Max !== "" ||
    filters.frequencies.length > 0;

  const columns = [
    {
      field: " ",
      headerName: "",
      sortable: false,
      width: 70,
      disableClickEventBubbling: true,
      renderCell: params => {
        return (
          <div className="d-flex justify-content-between align-items-center" style={{ cursor: "pointer" }}>
            <IconButton m={0.5} aria-label="primary" onClick={() => openOrder(params.row.id)}>
              <VisibilityIcon style={{ paddingRight: "10px" }} />
            </IconButton>
          </div>
        );
      }
    },
    { field: "id", headerName: t("table.id"), width: 70, type: "number", headerAlign: "left", align: "left", disableColumnMenu: true },
    {
      field: "created_at",
      headerName: t("table.creationDate"),
      width: 170,
      editable: false,
      headerAlign: "left",
      align: "left",
      type: "date",
      valueFormatter: params => moment(params?.value).format("YYYY-MM-DD")
    },
    {
      field: "user_name",
      headerName: t("table.customer"),
      width: 150,
      headerAlign: "left",
      align: "left",
      editable: false
    },
    {
      field: "email",
      headerName: t("table.email"),
      width: 280,
      headerAlign: "left",
      align: "left",
      editable: false
    },
    {
      field: "first_pickup",
      headerName: t("table.firstPickup"),
      width: 190,
      editable: false,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "pickup_date",
      headerName: t("table.pickupDate"),
      width: 200,
      editable: false,
      headerAlign: "left",
      align: "left",
      type: "date",
      valueFormatter: params => moment(params?.value).format("YYYY-MM-DD")
    },
    {
      field: "pickup_time",
      headerName: t("table.pickupTime"),
      width: 120,
      editable: false,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "status",
      headerName: t("table.status"),
      width: 130,
      editable: false,
      headerAlign: "left",
      align: "left",
      renderCell: params => {
        let icon;
        let color;
        switch (params.row.status) {
          case "cancelled":
            icon = <ReportProblemIcon style={{ color: "#d32f2f" }} />;
            color = "#d32f2f";
            break;
          case "processing":
            icon = <AutorenewIcon style={{ color: "#f57c00" }} />;
            color = "#f57c00";
            break;
          case "completed":
            icon = <CheckIcon style={{ color: "#388e3c" }} />;
            color = "#388e3c";
            break;
          case "confirmed":
            icon = <InfoIcon style={{ color: "#1976d2" }} />;
            color = "#1976d2";
            break;
          default:
            break;
        }
        return (
          <div className="d-flex justify-content-between align-items-center ">
            <Chip icon={icon} label={params.row.status} variant="outlined" style={{ color: color, borderColor: color }} />
          </div>
        );
      }
    },
    {
      field: "shipping_payment_status",
      headerName: t("table.paymentStatus"),
      width: 200,
      editable: false,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "sub_expiry_days",
      headerName: "Sub. termina",
      width: 140,
      editable: false,
      headerAlign: "left",
      align: "left",
      renderCell: params => {
        const days = params.row.sub_expiry_days;
        if (days === null || days === undefined || days > 30) return <span style={{ color: "#aaa" }}>—</span>;
        if (days < 0) return <Chip size="small" label="Expirada" style={{ backgroundColor: "#b71c1c", color: "#fff", fontWeight: 700, fontSize: 11, height: 20 }} />;
        const bg = days <= 7 ? "#d32f2f" : "#f57c00";
        const label = days === 0 ? "Hoje" : `${days}d`;
        return <Chip size="small" label={label} style={{ backgroundColor: bg, color: "#fff", fontWeight: 700, fontSize: 11, height: 20 }} />;
      }
    },
    {
      field: "subscribed_price",
      headerName: t("table.subscribedPrice"),
      width: 190,
      height: 50,
      editable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      type: "number",
      renderCell: params => {
        return <div className="d-flex justify-content-between align-items-center">{params.row?.subscribed_price ? params.row.subscribed_price + " €" : "-"}</div>;
      }
    },
    {
      field: "shipping_cost",
      headerName: t("table.shippingPrice"),
      width: 150,
      height: 50,
      editable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      type: "number",
      renderCell: params => {
        return <div className="d-flex justify-content-between align-items-center">{params.row?.shipping_cost ? params.row.shipping_cost + " €" : "-"}</div>;
      }
    },
    {
      field: "size_m2",
      headerName: t("table.sizeM2"),
      width: 200,
      editable: false,
      headerAlign: "left",
      align: "left",
      type: "number"
    },
    {
      field: "payment_frequency",
      headerName: "Contrato",
      width: 130,
      editable: false,
      headerAlign: "left",
      align: "left",
      renderCell: params => {
        const map = { monthly: "Mensal", quarterly: "Trimestral", semester: "Semestral", annual: "Anual" };
        const label = map[params.row.payment_frequency] || params.row.payment_frequency || "—";
        return <span style={{ fontSize: 13 }}>{label}</span>;
      }
    },
    {
      field: "service_type",
      headerName: t("table.serviceType"),
      width: 200,
      editable: false,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "products_count",
      headerName: t("table.products"),
      width: 130,
      editable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      type: "number"
    },
    {
      field: "items_count",
      headerName: t("table.items"),
      minWidth: 140,
      editable: false,
      headerAlign: "left",
      align: "left",
      type: "number"
    },
    {
      field: "utm_source",
      headerName: "Origem",
      width: 130,
      editable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderCell: params => {
        const source = params.row?.utm_source;
        if (!source || source === "-") return <span style={{ color: "#aaa" }}>—</span>;
        const colors = { instagram: "#E1306C", facebook: "#1877F2", google: "#34A853", tiktok: "#010101", linkedin: "#0A66C2", email: "#FF6B00" };
        const color = colors[source.toLowerCase()] || "#888";
        return (
          <span style={{ background: color + "22", color, border: "1px solid " + color + "66", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
            {source}
          </span>
        );
      }
    },
    {
      field: "utm_campaign",
      headerName: "Campanha",
      width: 160,
      editable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderCell: params => {
        const c = params.row?.utm_campaign;
        return <span style={{ color: c === "-" ? "#aaa" : "#333", fontSize: 12 }}>{c || "—"}</span>;
      }
    },
    {
      field: "carrier_name",
      headerName: t("table.carrier"),
      width: 140,
      editable: false,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "warehouse_name",
      headerName: t("table.warehouse"),
      width: 150,
      editable: false,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "city",
      headerName: t("table.city"),
      width: 140,
      editable: false,
      headerAlign: "left",
      align: "left"
    }
  ];

  const getServiceInfo = order => {
    const feeDetails =
      typeof order.one_time_fee_details === "string"
        ? (() => {
            try {
              return JSON.parse(order.one_time_fee_details || "{}");
            } catch {
              return {};
            }
          })()
        : order.one_time_fee_details || {};
    const items = feeDetails?.items || [];
    const services = order.services || [];
    const allItems = [
      ...items,
      ...services.map(s => ({ name: s.name, namePt: s.name, description: s.description || "" }))
    ];
    const hasPremium = allItems.some(
      i => (i.name || i.namePt || "").toLowerCase().includes("premium")
    );
    return {
      serviceType: hasPremium ? "Premium" : "Básico"
    };
  };

  const handleOrderAsColumn = async orders => {
    return orders.map(order => {
      const allItems = order?.products?.flatMap(product => product?.items || []) || [];
      const uniqueItems = uniqBy(allItems, "_id");
      const itemsCountFromProducts = uniqueItems.length;
      const itemsCountFromAPI = order?.items_count;
      const { serviceType } = getServiceInfo(order);
      const pickupDateParts = split(order?.pickup_date, "::");
      const sizeM2 = order.total_m2 ?? order.current_m2;

      return {
        id: order._id,
        created_at: moment(order.created_at).format("YYYY-MM-DD"),
        first_pickup: isBoolean(order?.is_first_order) ? (order?.is_first_order ? "Yes" : "No") : "-",
        pickup_date: moment(pickupDateParts[0] || order?.pickup_date).format("YYYY-MM-DD"),
        pickup_time: pickupDateParts[1] || "-",
        status: order.status,
        shipping_payment_status: order?.stripe_shipping_details?.p_intent_status || "NA",
        products_count: order?.products?.length || order?.products_count || 0,
        items_count: itemsCountFromAPI || itemsCountFromProducts || 0,
        user_name: order.user_name,
        email: order.email,
        subscribed_price: getDisplaySubscriptionValue(
          order.subscribed_price ?? order?.amount?.recurring?.value,
          order.payment_frequency,
          sizeM2
        ),
        shipping_cost: round(order.shipping_cost || order?.amount?.one_time?.value || 0, 2),
        carrier_name: order?.carrier_name || "NA",
        warehouse_name: order?.warehouse_name || "NA",
        city: order.city,
        size_m2: sizeM2 != null ? round(Number(sizeM2), 1) : "-",
        service_type: serviceType,
        payment_frequency: order.payment_frequency || "monthly",
        utm_source: order?.analytics?.utm_source || '-',
        utm_campaign: order?.analytics?.utm_campaign || '-',
        sub_expiry_days: (() => {
          const sub = subscriptions.find(s => String(s.order_id) === String(order._id));
          if (!sub?.current_period_end) return null;
          return Math.floor((sub.current_period_end * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
        })()
      };
    });
  };

  useEffect(() => {
    // Sem dados: carregar. Depois de um erro não repetir em ciclo (a equipa recarrega a página).
    if (ORDER_STATE.orders === null && !ORDER_STATE.fetchingOrdersLoading && !ORDER_STATE.fetchingOrdersError) {
      dispatch(getOrders({}));
    }
  }, [dispatch, ORDER_STATE.orders, ORDER_STATE.fetchingOrdersLoading, ORDER_STATE.fetchingOrdersError]);

  useEffect(() => {
    // Ao voltar à lista, atualizar o que já estava em memória; e a cada 2 minutos
    // para os pedidos novos aparecerem sem recarregar a página.
    if (ORDER_STATE.orders !== null) dispatch(getOrders({}));
    const refresh = setInterval(() => dispatch(getOrders({})), 2 * 60 * 1000);
    return () => clearInterval(refresh);
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!CUSTOMER_STATE.subscriptions && !CUSTOMER_STATE.fetchingSubscriptionsLoading && !CUSTOMER_STATE.fetchingSubscriptionsError) {
      dispatch(getSubscriptions({}));
    }
  }, [dispatch, CUSTOMER_STATE.subscriptions, CUSTOMER_STATE.fetchingSubscriptionsLoading, CUSTOMER_STATE.fetchingSubscriptionsError]);

  useEffect(() => {
    if (orders && orders.length > 0) {
      (async () => {
        const rows = await handleOrderAsColumn(orders);
        setRows(rows);
      })();
    }
  }, [orders, subscriptions]); // eslint-disable-line

  const openOrder = orderId => {
    history.push(`${history.location.pathname}/${orderId}`);
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <div className={classes.root}>

          {/* ── Filter bar ── */}
          <Paper className={classes.filterBar} elevation={0} variant="outlined">
            {/* Row 1: Status + Contrato */}
            <div className={classes.filterRow}>
              <span className={classes.filterLabel}>Estado</span>
              <div className={classes.filterChips}>
                {STATUSES.map(s => {
                  const active = filters.statuses.includes(s.value);
                  return (
                    <Chip
                      key={s.value}
                      label={s.label}
                      size="small"
                      onClick={() => setFilters(f => ({ ...f, statuses: toggleItem(f.statuses, s.value) }))}
                      style={{
                        backgroundColor: active ? s.color : "transparent",
                        color: active ? "#fff" : s.color,
                        border: `1px solid ${s.color}`,
                        fontWeight: active ? 700 : 400,
                        cursor: "pointer",
                      }}
                    />
                  );
                })}
              </div>
              <span style={{ width: 24 }} />
              <span className={classes.filterLabel}>Contrato</span>
              <div className={classes.filterChips}>
                {FREQUENCIES.map(f => {
                  const active = filters.frequencies.includes(f.value);
                  return (
                    <Chip
                      key={f.value}
                      label={f.label}
                      size="small"
                      onClick={() => setFilters(prev => ({ ...prev, frequencies: toggleItem(prev.frequencies, f.value) }))}
                      style={{
                        backgroundColor: active ? "#1976d2" : "transparent",
                        color: active ? "#fff" : "#1976d2",
                        border: "1px solid #1976d2",
                        fontWeight: active ? 700 : 400,
                        cursor: "pointer",
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Row 2: Date range + m² range + results + clear */}
            <div className={classes.filterRow}>
              <span className={classes.filterLabel}>Data recolha</span>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                label="De"
                InputLabelProps={{ shrink: true }}
                value={filters.dateFrom}
                onChange={e => { const v = e.target.value; setFilters(f => ({ ...f, dateFrom: v })); }}
                style={{ width: 150 }}
                inputProps={{ style: { fontSize: 13, padding: "6px 10px" } }}
              />
              <TextField
                type="date"
                size="small"
                variant="outlined"
                label="Até"
                InputLabelProps={{ shrink: true }}
                value={filters.dateTo}
                onChange={e => { const v = e.target.value; setFilters(f => ({ ...f, dateTo: v })); }}
                style={{ width: 150 }}
                inputProps={{ style: { fontSize: 13, padding: "6px 10px" } }}
              />
              <span style={{ width: 16 }} />
              <span className={classes.filterLabel}>m²</span>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                label="Mín"
                value={filters.m2Min}
                onChange={e => { const v = e.target.value; setFilters(f => ({ ...f, m2Min: v })); }}
                style={{ width: 90 }}
                inputProps={{ min: 0, style: { fontSize: 13, padding: "6px 10px" } }}
                InputProps={{ endAdornment: <InputAdornment position="end" style={{ fontSize: 11 }}>m²</InputAdornment> }}
              />
              <TextField
                type="number"
                size="small"
                variant="outlined"
                label="Máx"
                value={filters.m2Max}
                onChange={e => { const v = e.target.value; setFilters(f => ({ ...f, m2Max: v })); }}
                style={{ width: 90 }}
                inputProps={{ min: 0, style: { fontSize: 13, padding: "6px 10px" } }}
                InputProps={{ endAdornment: <InputAdornment position="end" style={{ fontSize: 11 }}>m²</InputAdornment> }}
              />
              <span className={classes.resultCount}>
                {hasActiveFilters ? `${filteredRows.length} de ${rows.length} pedidos` : `${rows.length} pedidos`}
              </span>
              {hasActiveFilters && (
                <button className={classes.clearBtn} onClick={() => setFilters(DEFAULT_FILTERS)}>
                  Limpar filtros
                </button>
              )}
            </div>
          </Paper>

          {/* ── Table ── */}
          {ORDER_STATE.fetchingOrdersLoading && isEmpty(orders) ? (
            <div className={classes.loading}>
              <Loading />
            </div>
          ) : (
            <div>
              {orders && orders.length > 0 ? (
                <MaterialDataGridTable rows={filteredRows} columns={columns} data={orders} setRows={setRows} handleDataAsColumn={handleOrderAsColumn} tableName="PICKUP_ORDERS_TABLE" />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <img src={NoData} alt="" />
                  <label style={{ fontSize: "larger", padding: "20px" }}>There is no any order</label>
                </div>
              )}
            </div>
          )}
        </div>
      </GridItem>
    </GridContainer>
  );
}
